import React from 'react';
import { downloadCSV } from '_utils/csv-export';
import { sanitizeExcelWorkbook } from '_utils/sanitize-spreadsheet-cell';

// DAA require the scorecard KPIs a second way: one row per calendar day, one
// column per KPI, run monthly. The scorecard table shows the same KPIs
// aggregated over the whole range, so this view transposes the per-day
// `dailyReports` each sub-report already returns (kept intact by
// stripRawData, which recomputes the per-day counts before dropping rawData).
//
// Column order mirrors the spreadsheet DAA circulated. Minutes and pass
// targets are NOT hard-coded here — they are read off the KPI payload so the
// headers can never drift from what the backend actually scored against.
const DAILY_COLUMN_GROUPS = [
    {
        group: 'External Point',
        summaryKey: 'externalCallPoint',
        columns: [
            { label: 'Pre-Advised', kpiKey: 'preAdvisedA' },
            { label: 'Pre-Advised', kpiKey: 'preAdvisedB' },
            { label: 'Not Pre-Advised', kpiKey: 'nonAdvisedA' },
        ],
    },
    {
        group: 'Terminal Reception',
        summaryKey: 'ecac',
        columns: [
            { label: 'Pre-Advised', kpiKey: 'preAdvisedA' },
            { label: 'Pre-Advised', kpiKey: 'preAdvisedB' },
            { label: 'Not Pre-Advised', kpiKey: 'nonAdvisedA' },
            { label: 'Not Pre-Advised', kpiKey: 'nonAdvisedB' },
        ],
    },
    {
        group: 'Outbound Boarding',
        summaryKey: 'boardingSla',
        // boarding targets are minutes BEFORE departure, shown as (-35) in the
        // DAA sheet
        negativeMinutes: true,
        columns: [
            { label: '<230 seats', kpiKey: 'lt230SeatsA' },
            { label: '<230 seats', kpiKey: 'lt230SeatsB' },
            { label: '≥230 seats', kpiKey: 'gte230SeatsA' },
            { label: '≥230 seats', kpiKey: 'gte230SeatsB' },
            { label: '<230 seats First Wave', kpiKey: 'firstWave' },
        ],
    },
    {
        group: 'Inbound',
        summaryKey: 'inboundSla',
        columns: [
            { label: '<230 seats', kpiKey: 'lt230SeatsA' },
            { label: '<230 seats', kpiKey: 'lt230SeatsB' },
            { label: '<230 seats all disembarked', kpiKey: 'lt230SeatsC' },
            { label: '≥230 seats', kpiKey: 'gte230SeatsA' },
            { label: '≥230 seats', kpiKey: 'gte230SeatsB' },
            { label: '≥230 seats all disembarked', kpiKey: 'gte230SeatsC' },
        ],
    },
    {
        group: 'Terminal Holding Point 218/407',
        summaryKey: 'terminalReception',
        columns: [
            { label: 'Pre-Advised', kpiKey: 'preAdvisedA' },
            { label: 'Pre-Advised', kpiKey: 'preAdvisedB' },
            { label: 'Not Pre-Advised', kpiKey: 'nonAdvisedA' },
            { label: 'Not Pre-Advised', kpiKey: 'nonAdvisedB' },
        ],
    },
    {
        group: 'Transfer Passengers',
        summaryKey: 'transferSla',
        columns: [
            { label: 'Pre-Advised', kpiKey: 'preAdvised' },
        ],
    },
];

const NA = 'NA';
const DATE_COL_WIDTH = 132;
const GROUP_ROW_HEIGHT = 30;

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// dailyReports dates are plain 'YYYY-MM-DD' strings — parse the parts rather
// than new Date(str), which would shift the day in negative-offset timezones.
function formatDate(dateStr) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateStr ?? ''));
    if (!m) return String(dateStr ?? '');
    const [, y, mo, d] = m;
    const weekday = WEEKDAYS[new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d))).getUTCDay()];
    return `${weekday} ${d}/${mo}/${y}`;
}

function targetNumber(kpi) {
    const raw = kpi?.target;
    if (raw == null) return null;
    const num = parseFloat(String(raw).replace('%', ''));
    return Number.isFinite(num) ? num : null;
}

function paxTotal(kpi) {
    return kpi?.total ?? kpi?.totalAssisted ?? null;
}

// A KPI with zero evaluated passengers cannot be breached, so it is reported
// as NA rather than 0% — same rule the scorecard table applies.
function cellState(kpi) {
    if (!kpi) return { display: NA, state: 'na', score: null };
    const total = paxTotal(kpi);
    if (total === 0) return { display: NA, state: 'na', score: null };
    const score = kpi.score ?? null;
    if (score == null) return { display: NA, state: 'na', score: null };
    const target = targetNumber(kpi);
    const met = target == null ? true : score >= target;
    return { display: `${score}%`, state: met ? 'pass' : 'fail', score };
}

// Header reads e.g. "Pre-Advised (10) T98%" — minutes and target come from any
// day that actually carries the KPI, falling back to the range summary.
function columnMeta(column, group, dailyByDate, summaries) {
    const fromSummary = summaries?.[group.summaryKey]?.kpis?.[column.kpiKey] ?? null;
    let source = fromSummary;
    if (source?.minutes == null || source?.target == null) {
        const days = dailyByDate?.[group.summaryKey];
        if (days) {
            const found = Object.values(days)
                .map((day) => day?.kpis?.[column.kpiKey])
                .find((kpi) => kpi?.minutes != null || kpi?.target != null);
            if (found) source = { ...(source || {}), ...found };
        }
    }
    const minutes = source?.minutes ?? null;
    const target = source?.target ?? null;
    const minutesLabel = minutes == null ? null : `(${group.negativeMinutes ? '-' : ''}${minutes})`;
    const targetLabel = target == null ? null : `T${target}`;
    return {
        minutes,
        target,
        targetNum: targetNumber(source),
        header: [column.label, minutesLabel, targetLabel].filter(Boolean).join(' '),
        plainHeader: [group.group, column.label, minutesLabel, targetLabel].filter(Boolean).join(' '),
    };
}

function buildMatrix({ daily, summaries }) {
    // index each sub-report's dailyReports by date for O(1) lookup per cell
    const dailyByDate = {};
    Object.keys(daily || {}).forEach((key) => {
        const byDate = {};
        (daily[key] || []).forEach((report) => {
            if (report?.date) byDate[report.date] = report;
        });
        dailyByDate[key] = byDate;
    });

    const dates = [...new Set(Object.values(dailyByDate).flatMap((byDate) => Object.keys(byDate)))].sort();

    const groups = DAILY_COLUMN_GROUPS.map((group) => ({
        ...group,
        columns: group.columns.map((column) => ({
            ...column,
            ...columnMeta(column, group, dailyByDate, summaries),
        })),
    }));

    const flatColumns = groups.flatMap((group) =>
        group.columns.map((column) => ({ ...column, group, summaryKey: group.summaryKey }))
    );

    const rows = dates.map((date) => ({
        date,
        cells: flatColumns.map((column) =>
            cellState(dailyByDate[column.summaryKey]?.[date]?.kpis?.[column.kpiKey])
        ),
    }));

    // range-level figure per column, straight off each sub-report summary, so
    // the monthly line matches the scorecard exactly
    const monthly = flatColumns.map((column) =>
        cellState(summaries?.[column.summaryKey]?.kpis?.[column.kpiKey])
    );

    const daysBelow = flatColumns.map((_, i) => rows.filter((row) => row.cells[i].state === 'fail').length);

    return { groups, flatColumns, rows, monthly, daysBelow };
}

const wrapperStyle = {
    marginBottom: '24px',
};

const scrollStyle = {
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: '70vh',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
};

const tableStyle = {
    borderCollapse: 'separate',
    borderSpacing: 0,
    width: 'max-content',
    minWidth: '100%',
};

const groupHeaderStyle = {
    position: 'sticky',
    top: 0,
    zIndex: 3,
    height: `${GROUP_ROW_HEIGHT}px`,
    border: '1px solid #6b8eb5',
    padding: '4px 8px',
    textAlign: 'center',
    backgroundColor: '#1e3a5f',
    color: '#fff',
    fontWeight: 700,
    fontSize: '12px',
    whiteSpace: 'nowrap',
};

const columnHeaderStyle = {
    position: 'sticky',
    top: `${GROUP_ROW_HEIGHT}px`,
    zIndex: 3,
    border: '1px solid #6b8eb5',
    padding: '6px 8px',
    textAlign: 'center',
    backgroundColor: '#6b8eb5',
    color: '#fff',
    fontWeight: 600,
    fontSize: '11px',
    width: '96px',
    minWidth: '96px',
    verticalAlign: 'bottom',
};

const dateCornerStyle = {
    ...groupHeaderStyle,
    left: 0,
    zIndex: 5,
    textAlign: 'left',
    width: `${DATE_COL_WIDTH}px`,
    minWidth: `${DATE_COL_WIDTH}px`,
};

const dateCornerSubStyle = {
    ...columnHeaderStyle,
    left: 0,
    zIndex: 5,
    textAlign: 'left',
    backgroundColor: '#6b8eb5',
    width: `${DATE_COL_WIDTH}px`,
    minWidth: `${DATE_COL_WIDTH}px`,
};

const dateCellStyle = {
    position: 'sticky',
    left: 0,
    zIndex: 2,
    border: '1px solid #e5e7eb',
    padding: '6px 10px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: 600,
    color: '#1f2937',
    backgroundColor: '#fff',
    whiteSpace: 'nowrap',
    width: `${DATE_COL_WIDTH}px`,
    minWidth: `${DATE_COL_WIDTH}px`,
};

const valueCellStyle = {
    border: '1px solid #e5e7eb',
    padding: '6px 8px',
    textAlign: 'center',
    fontSize: '12px',
    color: '#1f2937',
    backgroundColor: '#fff',
};

const CELL_STATE_STYLE = {
    pass: { color: '#1f2937' },
    fail: { color: '#b91c1c', backgroundColor: '#fee2e2', fontWeight: 700 },
    na: { color: '#9ca3af' },
};

const footerLabelStyle = {
    ...dateCellStyle,
    backgroundColor: '#e5e7eb',
    fontWeight: 700,
};

const footerCellStyle = {
    ...valueCellStyle,
    backgroundColor: '#e5e7eb',
    fontWeight: 700,
};

const buttonStyle = {
    padding: '4px 10px',
    fontSize: '12px',
    cursor: 'pointer',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
};

const DublinScorecardDailyTable = ({ daily, summaries, errors }) => {
    const hasAnyDaily = daily && Object.values(daily).some((d) => Array.isArray(d) && d.length > 0);
    if (!hasAnyDaily) {
        return <p style={{ fontSize: '14px', color: '#6b7280' }}>No daily data available. Run the report to see results.</p>;
    }

    const { groups, flatColumns, rows, monthly, daysBelow } = buildMatrix({ daily, summaries });

    if (!rows.length) {
        return <p style={{ fontSize: '14px', color: '#6b7280' }}>No days returned for the selected range.</p>;
    }

    const buildExportRows = () => {
        const toRow = (label, cells) => {
            const row = { Date: label };
            flatColumns.forEach((column, i) => {
                row[column.plainHeader] = cells[i].display;
            });
            return row;
        };
        const exportRows = rows.map((row) => toRow(formatDate(row.date), row.cells));
        exportRows.push(toRow('Monthly', monthly));
        const belowRow = { Date: 'Days below target' };
        flatColumns.forEach((column, i) => { belowRow[column.plainHeader] = daysBelow[i]; });
        exportRows.push(belowRow);
        return exportRows;
    };

    const fileStamp = `${rows[0].date}_${rows[rows.length - 1].date}`;

    const handleExportCsv = () => {
        downloadCSV(buildExportRows(), `dublin-daily-kpi-${fileStamp}.csv`);
    };

    const handleExportExcel = async () => {
        const Excel = await import('exceljs/dist/exceljs.min.js');
        const workbook = new Excel.Workbook();
        const sheet = workbook.addWorksheet('Daily KPI');

        const border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        const COLORS = {
            title: 'FF1E3A5F',
            colHeader: 'FF6B8EB5',
            footer: 'FFE5E7EB',
            whiteText: 'FFFFFFFF',
            red: 'FFFEE2E2',
            redText: 'FFB91C1C',
            greyText: 'FF9CA3AF',
        };
        const applyCell = (cell, { fill, font, align = 'center' } = {}) => {
            cell.border = border;
            cell.alignment = { vertical: 'middle', horizontal: align, wrapText: true };
            if (fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
            if (font) cell.font = font;
        };

        const totalCols = flatColumns.length + 1;
        sheet.getColumn(1).width = 20;
        for (let c = 2; c <= totalCols; c++) sheet.getColumn(c).width = 15;

        // group header row: one merged cell per KPI group
        const groupRow = sheet.addRow(['Date', ...flatColumns.map(() => '')]);
        applyCell(groupRow.getCell(1), { fill: COLORS.title, font: { bold: true, color: { argb: COLORS.whiteText } }, align: 'left' });
        let col = 2;
        groups.forEach((group) => {
            const span = group.columns.length;
            const first = col;
            const last = col + span - 1;
            groupRow.getCell(first).value = group.group;
            for (let c = first; c <= last; c++) {
                applyCell(groupRow.getCell(c), { fill: COLORS.title, font: { bold: true, color: { argb: COLORS.whiteText } } });
            }
            if (span > 1) sheet.mergeCells(groupRow.number, first, groupRow.number, last);
            col = last + 1;
        });
        groupRow.height = 22;

        const headerRow = sheet.addRow(['', ...flatColumns.map((c) => c.header)]);
        for (let c = 1; c <= totalCols; c++) {
            applyCell(headerRow.getCell(c), { fill: COLORS.colHeader, font: { bold: true, color: { argb: COLORS.whiteText }, size: 10 } });
        }
        headerRow.height = 42;

        rows.forEach((row) => {
            const excelRow = sheet.addRow([formatDate(row.date), ...row.cells.map((cell) => cell.display)]);
            applyCell(excelRow.getCell(1), { font: { bold: true }, align: 'left' });
            row.cells.forEach((cell, i) => {
                applyCell(excelRow.getCell(i + 2), {
                    fill: cell.state === 'fail' ? COLORS.red : undefined,
                    font: cell.state === 'fail'
                        ? { bold: true, color: { argb: COLORS.redText } }
                        : cell.state === 'na' ? { color: { argb: COLORS.greyText } } : undefined,
                });
            });
            excelRow.height = 20;
        });

        const monthlyRow = sheet.addRow(['Monthly', ...monthly.map((cell) => cell.display)]);
        applyCell(monthlyRow.getCell(1), { fill: COLORS.footer, font: { bold: true }, align: 'left' });
        monthly.forEach((cell, i) => {
            applyCell(monthlyRow.getCell(i + 2), {
                fill: cell.state === 'fail' ? COLORS.red : COLORS.footer,
                font: { bold: true, color: { argb: cell.state === 'fail' ? COLORS.redText : undefined } },
            });
        });
        monthlyRow.height = 20;

        const belowRow = sheet.addRow(['Days below target', ...daysBelow]);
        applyCell(belowRow.getCell(1), { fill: COLORS.footer, font: { bold: true }, align: 'left' });
        daysBelow.forEach((_, i) => applyCell(belowRow.getCell(i + 2), { fill: COLORS.footer, font: { bold: true } }));
        belowRow.height = 20;

        sheet.views = [{ state: 'frozen', xSplit: 1, ySplit: 2 }];

        sanitizeExcelWorkbook(workbook);
        const buf = await workbook.xlsx.writeBuffer();
        const link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
        link.download = `dublin-daily-kpi-${fileStamp}.xlsx`;
        link.click();
        URL.revokeObjectURL(link.href);
    };

    const errorKeys = errors ? Object.keys(errors).filter((k) => errors[k]) : [];

    return (
        <div style={wrapperStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>
                    One row per day, one column per KPI. <strong>NA</strong> means no passengers were evaluated for that
                    KPI on that day, so the SLA could not be breached.
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button type="button" onClick={handleExportCsv} style={buttonStyle}>Export CSV</button>
                    <button
                        type="button"
                        onClick={handleExportExcel}
                        style={{ ...buttonStyle, backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }}
                    >
                        Export Excel
                    </button>
                </div>
            </div>

            {errorKeys.map((k) => (
                <p key={k} style={{ margin: '0 0 6px', fontSize: '12px', color: '#dc2626' }}>
                    {k} data failed: {errors[k]} — its columns show NA.
                </p>
            ))}

            <div style={scrollStyle}>
                <table style={tableStyle}>
                    <thead>
                        <tr>
                            <th style={dateCornerStyle} rowSpan={1}>Date</th>
                            {groups.map((group) => (
                                <th key={group.group} style={groupHeaderStyle} colSpan={group.columns.length}>
                                    {group.group}
                                </th>
                            ))}
                        </tr>
                        <tr>
                            <th style={dateCornerSubStyle} />
                            {flatColumns.map((column, i) => (
                                <th key={`${column.summaryKey}-${column.kpiKey}-${i}`} style={columnHeaderStyle}>
                                    {column.header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.date}>
                                <td style={dateCellStyle}>{formatDate(row.date)}</td>
                                {row.cells.map((cell, i) => (
                                    <td
                                        key={`${row.date}-${i}`}
                                        style={{ ...valueCellStyle, ...CELL_STATE_STYLE[cell.state] }}
                                    >
                                        {cell.display}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        <tr>
                            <td style={footerLabelStyle}>Monthly</td>
                            {monthly.map((cell, i) => (
                                <td
                                    key={`monthly-${i}`}
                                    style={{
                                        ...footerCellStyle,
                                        ...(cell.state === 'fail' ? { color: '#b91c1c', backgroundColor: '#fee2e2' } : null),
                                        ...(cell.state === 'na' ? { color: '#9ca3af' } : null),
                                    }}
                                >
                                    {cell.display}
                                </td>
                            ))}
                        </tr>
                        <tr>
                            <td style={footerLabelStyle}>Days below target</td>
                            {daysBelow.map((count, i) => (
                                <td
                                    key={`below-${i}`}
                                    style={{ ...footerCellStyle, color: count > 0 ? '#b91c1c' : '#1f2937' }}
                                >
                                    {count}
                                </td>
                            ))}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default DublinScorecardDailyTable;
